import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import {
  apiErrorStatus,
  apiErrorText,
  useCreateFleetGroupMutation,
  useLazyGetFleetGroupQuery,
  useListFleetModelsQuery,
  useUpdateFleetGroupMutation,
  type FleetGroupDetail,
} from '@shared/api';
import { pluralizeRu } from '@shared/lib/formatFile';
import { Button } from '@shared/ui/Button';
import { DrawerBody, DrawerFooter } from '@shared/ui/Drawer';
import {
  Actions,
  BlockHeading,
  BlockSection,
  ChoiceCard,
  ChoiceFieldset,
  ChoiceGrid,
  Control,
  ControlArea,
  FieldError,
  FieldHint,
  FieldLabel,
  FooterStatus,
  FormGrid,
  FormRow,
  GROUP_FIELD_LABEL,
  GROUP_UNITS,
  GROUP_UNIT_HINT,
  Notice,
  TextButton,
  changeCount,
  fleetType,
  groupChanges,
  groupDraft,
  groupPayload,
  groupUnitLabel,
  nameKey,
  rebaseGroupDraft,
  typo,
  type GroupDraft,
  type GroupFieldKey,
} from '@entities/fleet';
import { ModelsEditor, type ModelInfo } from './ModelsEditor';
import { PositionsEditor } from './PositionsEditor';

const Notices = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};

  &:empty {
    display: none;
  }
`;

const Count = styled.span`
  font-size: ${fleetType.small};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ConflictNote = styled.p`
  margin: ${({ theme }) => `${theme.spacing(1)} 0 0`};
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  border-left: 2px solid ${({ theme }) => theme.colors.status.warning};
  background: ${({ theme }) => theme.colors.status.warning}14;
  font-size: ${fleetType.small};
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.primary};
`;

function unique(values: readonly (string | null)[]): string[] {
  const seen = new Map<string, string>();
  for (const v of values) {
    const t = v?.trim();
    if (t && !seen.has(nameKey(t))) seen.set(nameKey(t), t);
  }
  return Array.from(seen.values()).sort((a, b) => a.localeCompare(b, 'ru'));
}

/** Текстовые поля формы — все, кроме единицы учёта (у неё свой переключатель). */
type TextFieldKey = Exclude<GroupFieldKey, 'unit'>;

interface GroupFormProps {
  /** Группа, которую правят; `null` — новая. */
  detail: FleetGroupDetail | null;
  formId: string;
  onCancel: () => void;
  /** `note` — что ещё сказать после сохранения: «перенесено из других групп». */
  onSaved: (detail: FleetGroupDetail, note: string | null) => void;
  /** Сколько правок — панель спрашивает перед закрытием. */
  onDirtyChange: (count: number) => void;
}

/**
 * Форма группы: название, учёт, коэффициент, доля простоя, должность паспорта,
 * должности Хронума и состав моделей. При правке уходят только изменённые
 * поля. На 409 (группу изменили, пока шла правка) — «Перечитать»: свежая
 * версия становится основой, правки человека переносятся поверх.
 */
export function GroupForm({ detail, formId, onCancel, onSaved, onDirtyChange }: GroupFormProps) {
  const [base, setBase] = useState<FleetGroupDetail | null>(detail);
  const [initial, setInitial] = useState<GroupDraft>(() => groupDraft(detail));
  const [draft, setDraft] = useState<GroupDraft>(initial);
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());
  const [showAll, setShowAll] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ message: string; fresh: FleetGroupDetail } | null>(
    null,
  );
  const [conflictValues, setConflictValues] = useState<Partial<Record<GroupFieldKey, string>>>({});
  const [positionsConflict, setPositionsConflict] = useState(false);
  const [carried, setCarried] = useState<number | null>(null);

  const [createGroup, { isLoading: creating }] = useCreateFleetGroupMutation();
  const [updateGroup, { isLoading: updating }] = useUpdateFleetGroupMutation();
  const [fetchGroup, { isFetching: rereading }] = useLazyGetFleetGroupQuery();
  // Список справочника: модели для добавления, их группа сейчас и ПРЦ (на сегодня и будущая).
  const { data: fleet } = useListFleetModelsQuery({});
  const busy = creating || updating || rereading;
  const isNew = base === null;

  const changes = useMemo(() => groupChanges(draft, initial), [draft, initial]);
  const count = changeCount(changes);
  const payload = useMemo(() => groupPayload(draft, initial, isNew), [draft, initial, isNew]);
  const issues = useMemo(() => new Map(payload.issues.map((i) => [i.key, i.message])), [payload]);
  const groupId = base?.group.id ?? null;
  const fleetById = useMemo(() => new Map((fleet?.items ?? []).map((m) => [m.id, m])), [fleet]);
  const moved = changes.added.filter((id) => {
    const g = fleetById.get(id)?.group;
    return g != null && g.id !== groupId;
  });

  useEffect(() => {
    onDirtyChange(count);
  }, [count, onDirtyChange]);

  // Обновление страницы или закрытие вкладки с несохранённой правкой.
  useEffect(() => {
    if (count === 0) return;
    const guard = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [count]);

  // Отказ сервера — плашку в поле зрения, при конфликте фокус на «Перечитать».
  const noticesRef = useRef<HTMLDivElement>(null);
  const rereadRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!conflict && !serverError) return;
    noticesRef.current?.scrollIntoView({ block: 'start' });
    if (conflict) rereadRef.current?.focus({ preventScroll: true });
  }, [conflict, serverError]);

  const groupModels = useMemo(
    () => new Map((base?.models ?? []).map((m) => [m.model_id, m])),
    [base],
  );
  // Справочник знает и группу модели, и будущую ПРЦ; модели в архиве в нём
  // нет — о них говорит карточка группы.
  const info = useCallback(
    (id: string): ModelInfo | null => {
      const f = fleetById.get(id);
      if (f) {
        return {
          id,
          name: f.name,
          category: f.category_name,
          units: f.units_count,
          status: f.status,
          prcPrice: f.prc?.price ?? null,
          prcUpcomingFrom: f.prc_upcoming?.valid_from ?? null,
          group: f.group ?? null,
        };
      }
      const g = groupModels.get(id);
      if (!g) return null;
      return {
        id,
        name: g.name,
        category: g.category_name,
        units: g.units_count,
        status: g.status,
        prcPrice: g.prc_price,
        prcUpcomingFrom: null,
        group: null,
      };
    },
    [groupModels, fleetById],
  );
  const positionSuggestions = useMemo(
    () => unique((fleet?.items ?? []).map((m) => m.operator_position)),
    [fleet],
  );

  const domId = useCallback((key: string) => `${formId}-${key}`, [formId]);
  const fieldError = (key: string) => ((showAll || touched.has(key)) && issues.get(key)) || null;

  function setText(key: TextFieldKey, value: string) {
    setDraft((d) => ({ ...d, values: { ...d.values, [key]: value } }));
  }

  function touch(key: string) {
    setTouched((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }

  function takeConflict(key: GroupFieldKey) {
    const fresh = conflictValues[key];
    if (fresh === undefined) return;
    setDraft((d) => {
      if (key !== 'unit') return { ...d, values: { ...d.values, [key]: fresh } };
      return fresh === 'day' || fresh === 'machine_hour'
        ? { ...d, values: { ...d.values, unit: fresh } }
        : d;
    });
    setConflictValues((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function focusIssue(key: string) {
    const el = document.getElementById(domId(key.replace(/:/g, '-')));
    if (!el) return;
    el.scrollIntoView({ block: 'center' });
    el.focus({ preventScroll: true });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setServerError(null);
    const first = payload.issues[0];
    if (first) {
      setShowAll(true);
      focusIssue(first.key);
      return;
    }
    if (base && count === 0) {
      onCancel();
      return;
    }
    try {
      const res = base
        ? await updateGroup({
            id: base.group.id,
            data: payload.data,
            expected_updated_at: base.group.updated_at,
          }).unwrap()
        : await createGroup(payload.data).unwrap();
      onSaved(res, moved.length > 0 ? `Перенесено из других групп: ${moved.length}` : null);
    } catch (err) {
      if (base && apiErrorStatus(err) === 409) {
        // 409 — и «группу изменили», и «название занято», и «должность в другой
        // группе». Различаем по факту: изменилась ли группа на сервере.
        const fresh = await fetchGroup(base.group.id)
          .unwrap()
          .catch(() => null);
        if (fresh && fresh.group.updated_at !== base.group.updated_at) {
          // Своими словами: текст сервера «откройте её заново» спорит с «Перечитать».
          setConflict({ message: 'Пока вы правили, группу изменил кто-то другой.', fresh });
          return;
        }
      }
      setServerError(
        apiErrorText(err, base ? 'Не удалось сохранить группу.' : 'Не удалось добавить группу.'),
      );
    }
  }

  function reread() {
    if (!conflict) return;
    const r = rebaseGroupDraft(draft, initial, conflict.fresh);
    setBase(conflict.fresh);
    setInitial(r.initial);
    setDraft(r.draft);
    setConflictValues(r.conflicts);
    setPositionsConflict(r.positionsConflict);
    setCarried(changeCount(groupChanges(r.draft, r.initial)));
    setConflict(null);
  }

  function conflictNote(key: GroupFieldKey) {
    const fresh = conflictValues[key];
    if (fresh === undefined) return null;
    const shown = key === 'unit' ? groupUnitLabel(fresh) : fresh === '' ? 'пусто' : fresh;
    return (
      <ConflictNote id={`${domId(key)}-conflict`}>
        Пока вы правили, в группе стало: <strong>{shown}</strong>. Сохранится ваше значение.{' '}
        <TextButton type="button" onClick={() => takeConflict(key)}>
          Взять значение коллеги
        </TextButton>
      </ConflictNote>
    );
  }

  /** Подпись, поле, подсказка, ошибка и конфликт текстового поля. */
  function textField(
    key: TextFieldKey,
    opts: { required?: boolean; hint?: string; numeric?: boolean; wide?: boolean; list?: string },
  ) {
    const error = fieldError(key);
    const id = domId(key);
    const described = [
      opts.hint ? `${id}-hint` : null,
      error ? `${id}-error` : null,
      conflictValues[key] !== undefined ? `${id}-conflict` : null,
    ]
      .filter((s): s is string => s !== null)
      .join(' ');
    return (
      <FormRow $wide={opts.wide}>
        <FieldLabel htmlFor={id}>
          <span>
            {GROUP_FIELD_LABEL[key]}
            {key === 'downtimePercent' && ', %'}
            {opts.required && (
              <span className="req" aria-hidden="true">
                {' '}
                *
              </span>
            )}
          </span>
        </FieldLabel>
        {key === 'comment' ? (
          <ControlArea
            id={id}
            rows={2}
            value={draft.values.comment}
            aria-describedby={described || undefined}
            onChange={(e) => setText(key, e.target.value)}
          />
        ) : (
          <Control
            id={id}
            value={draft.values[key]}
            type="text"
            inputMode={opts.numeric ? 'decimal' : undefined}
            autoComplete="off"
            spellCheck={opts.numeric ? false : undefined}
            list={opts.list}
            aria-required={opts.required ? 'true' : undefined}
            aria-invalid={error ? true : undefined}
            aria-describedby={described || undefined}
            onChange={(e) => setText(key, e.target.value)}
            onBlur={() => touch(key)}
          />
        )}
        {opts.hint && <FieldHint id={`${id}-hint`}>{opts.hint}</FieldHint>}
        {error && <FieldError id={`${id}-error`}>{error}</FieldError>}
        {conflictNote(key)}
      </FormRow>
    );
  }

  const day = draft.values.unit === 'day';
  const issueCount = payload.issues.length;
  const footerText =
    showAll && issueCount > 0
      ? `Проверьте ${issueCount} ${pluralizeRu(issueCount, ['поле', 'поля', 'полей'])}`
      : count > 0
        ? `Изменений: ${count}${moved.length > 0 ? ` · перенос из других групп: ${moved.length}` : ''}`
        : base
          ? 'Изменений нет'
          : 'Укажите название — остальное можно дописать потом';
  const positionsListId = domId('operator-positions');

  return (
    <>
      <DrawerBody>
        <Notices ref={noticesRef}>
          {conflict && (
            <Notice $tone="warning" role="alert">
              <span className="text">
                <strong>{conflict.message}</strong>{' '}
                {typo(
                  'Перечитайте группу: свежая версия станет основой, ваши правки перенесутся поверх неё.',
                )}
              </span>
              <Button
                ref={rereadRef}
                type="button"
                $variant="secondary"
                $size="sm"
                onClick={reread}
              >
                Перечитать
              </Button>
            </Notice>
          )}
          {carried !== null && (
            <Notice $tone="info" role="status">
              <span className="text">
                {typo(
                  `Загружена свежая версия группы. Перенесено ваших правок: ${carried}. ${
                    Object.keys(conflictValues).length > 0 || positionsConflict
                      ? 'Там, где коллега тоже менял значение, — отметки ниже.'
                      : 'Проверьте и сохраните ещё раз.'
                  }`,
                )}
              </span>
            </Notice>
          )}
          {serverError && (
            <Notice $tone="danger" role="alert">
              <span className="text">{serverError}</span>
            </Notice>
          )}
        </Notices>

        <form id={formId} onSubmit={submit} noValidate>
          <BlockSection aria-labelledby={`${formId}-main-h`}>
            <BlockHeading id={`${formId}-main-h`}>Основное</BlockHeading>
            <FormGrid>
              {textField('name', {
                required: true,
                wide: true,
                hint: typo('Профессия механизатора, как её знает Хронум: «Машинисты бульдозеров».'),
              })}
              <FormRow $wide>
                <ChoiceFieldset
                  aria-describedby={
                    conflictValues.unit !== undefined ? `${domId('unit')}-conflict` : undefined
                  }
                >
                  <legend>{GROUP_FIELD_LABEL.unit}</legend>
                  <ChoiceGrid>
                    {GROUP_UNITS.map((u) => (
                      <ChoiceCard key={u}>
                        <input
                          type="radio"
                          name={domId('unit')}
                          value={u}
                          checked={draft.values.unit === u}
                          onChange={() =>
                            setDraft((d) => ({ ...d, values: { ...d.values, unit: u } }))
                          }
                        />
                        <span className="title">{groupUnitLabel(u)}</span>
                        <span className="hint">{typo(GROUP_UNIT_HINT[u])}</span>
                      </ChoiceCard>
                    ))}
                  </ChoiceGrid>
                </ChoiceFieldset>
                {conflictNote('unit')}
              </FormRow>
              {textField('hoursCoef', {
                numeric: true,
                hint: day
                  ? 'При учёте в сутках не применяется.'
                  : typo('Сколько маш.-часов даёт час работы механизатора. Обычно 1.'),
              })}
              {textField('downtimePercent', {
                numeric: true,
                hint: typo(
                  'Час простоя оплачивается этой долей ставки, в январском БДР — 30 %. Пока часов простоя нет, все часы считаются работой.',
                ),
              })}
              {textField('operatorPosition', {
                wide: true,
                list: positionsListId,
                hint: typo(
                  'По ней «Подобрать по должностям механизаторов» находит модели для группы. Пусто — группа без механизатора.',
                ),
              })}
              {textField('comment', { wide: true })}
            </FormGrid>
          </BlockSection>

          <BlockSection aria-labelledby={`${formId}-positions-h`}>
            <BlockHeading id={`${formId}-positions-h`}>
              Должности Хронума <Count>{draft.positions.length}</Count>
            </BlockHeading>
            <PositionsEditor
              rows={draft.positions}
              onChange={(positions) => setDraft((d) => ({ ...d, positions }))}
              error={(key) => (showAll ? (issues.get(key) ?? null) : null)}
              domId={domId}
              suggestions={positionSuggestions}
              passportPosition={draft.values.operatorPosition}
              conflict={positionsConflict}
            />
          </BlockSection>

          <BlockSection aria-labelledby={`${formId}-models-h`}>
            <BlockHeading id={`${formId}-models-h`}>
              Модели группы <Count>{draft.modelIds.length}</Count>
            </BlockHeading>
            <ModelsEditor
              groupId={groupId}
              modelIds={draft.modelIds}
              initialIds={initial.modelIds}
              info={info}
              fleet={fleet?.items ?? []}
              onChange={(modelIds) => setDraft((d) => ({ ...d, modelIds }))}
              domId={domId}
            />
          </BlockSection>

          <datalist id={positionsListId}>
            {positionSuggestions.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
        </form>
      </DrawerBody>
      <DrawerFooter>
        <FooterStatus role="status">
          {conflict ? 'Группу изменили, пока шла правка' : footerText}
          {conflict && (
            <>
              {' · '}
              <TextButton type="button" onClick={reread}>
                перечитать
              </TextButton>
            </>
          )}
          {!conflict && showAll && issueCount > 0 && (
            <>
              {' · '}
              <TextButton
                type="button"
                onClick={() => {
                  const first = payload.issues[0];
                  if (first) focusIssue(first.key);
                }}
              >
                к первому
              </TextButton>
            </>
          )}
        </FooterStatus>
        <Actions>
          <Button type="button" $variant="ghost" onClick={onCancel} disabled={busy}>
            Отмена
          </Button>
          <Button
            type="submit"
            form={formId}
            $variant="primary"
            disabled={busy || conflict !== null}
          >
            {creating || updating ? 'Сохраняю…' : base ? 'Сохранить' : 'Добавить группу'}
          </Button>
        </Actions>
      </DrawerFooter>
    </>
  );
}
